import type { ApiResponse } from "@/types";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useRef } from "react";
import { useShallow } from "zustand/react/shallow";
import type { FpMessage } from "../../../schemas/codegen";
import { useMessageStore } from "../../state/stores/messageStore";
import { api } from "../api/api";

type MessagesResponse = ApiResponse<{
  messages: FpMessage[];
}>;

export function useMessageCacheSync(activeChatId: string | undefined) {
  const { setMessages, clearChatData, setLoading } = useMessageStore(
    useShallow((state) => ({
      setLoading: state.setLoading,
      setMessages: state.setMessages,
      clearChatData: state.clearChatData,
    })),
  );

  const previousChatIdRef = useRef<string | undefined>(undefined);

  const { data: cachedMessages, refetch } = useQuery<MessagesResponse>({
    queryKey: ["messages", activeChatId],
    queryFn: async () => {
      setLoading(true);
      if (!activeChatId) {
        return { data: { messages: [] } };
      }
      const result = await api.listMessages(activeChatId);
      setLoading(false);
      return result;
    },
    enabled: !!activeChatId,
    gcTime: 1000 * 60 * 30, // Keep in cache for 30 minutes
    staleTime: 0, // Always consider data stale to trigger background refetches
    refetchOnMount: true, // Refetch when component mounts
    refetchOnWindowFocus: true, // Refetch when window regains focus
  });

  // Handle chat switching and cache updates
  useEffect(() => {
    const isChatSwitch =
      previousChatIdRef.current !== undefined &&
      activeChatId !== previousChatIdRef.current;

    if (isChatSwitch) {
      if (!cachedMessages?.data.messages) {
        clearChatData();
        return;
      }

      // For chat switches, directly set the messages
      setMessages(cachedMessages.data.messages);
      // Trigger a background refetch
      refetch();
    } else {
      if (!cachedMessages?.data.messages) {
        return;
      }

      // For regular updates, merge with existing messages
      const currentMessages = useMessageStore.getState().messages;
      const currentMessageMap = new Map(
        currentMessages.map((msg: FpMessage) => [msg.id, msg]),
      );

      const mergedMessages = cachedMessages.data.messages.map(
        (cachedMsg: FpMessage) => {
          const currentMsg = currentMessageMap.get(cachedMsg.id);
          if (
            currentMsg &&
            // TODO: Add more robust comparison logic
            // For now: more content equals newer.
            currentMsg.content.length > cachedMsg.content.length
          ) {
            return currentMsg;
          }
          return cachedMsg;
        },
      );

      const newMessages = currentMessages.filter(
        (msg: FpMessage) =>
          !cachedMessages.data.messages.some(
            (cached: FpMessage) => cached.id === msg.id,
          ),
      );

      setMessages([...mergedMessages, ...newMessages]);
    }

    previousChatIdRef.current = activeChatId;
  }, [activeChatId, cachedMessages, setMessages, refetch, clearChatData]);

  return {
    refetch, // Expose refetch for manual refreshes if needed
  };
}
